import { useState, useCallback } from "react";
import type {
  DocumentTypeDetailViewModel,
  UpdateDocumentTypeRequest,
  CreateDocumentTypeRequest,
} from "@models/doctype";
import { docTypeApi } from "@src/services/doctypeApi";

export interface IDoctypeState {
  doctypesDetail: DocumentTypeDetailViewModel[];
  isLoading: boolean;
  error: string | null;
  fetchDoctypesDetailByProject: () => Promise<void>;
  createDoctype: (
    data: CreateDocumentTypeRequest,
  ) => Promise<DocumentTypeDetailViewModel>;
  updateDoctype: (
    docTypeId: string,
    data: UpdateDocumentTypeRequest,
  ) => Promise<DocumentTypeDetailViewModel>;
  deleteDoctype: (docTypeId: string) => Promise<void>;
}

export const useDoctype = (): IDoctypeState => {
  const [doctypesDetail, setDoctypesDetail] = useState<
    DocumentTypeDetailViewModel[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDoctypesDetailByProject = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await docTypeApi.getDoctypesDetailByProject();
      setDoctypesDetail(response);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to fetch detailed document types";
      setError(errorMessage);
      console.error("Error fetching detailed document types:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createDoctype = useCallback(
    async (
      data: CreateDocumentTypeRequest,
    ): Promise<DocumentTypeDetailViewModel> => {
      try {
        setIsLoading(true);
        setError(null);
        const newDoctype = await docTypeApi.createDocumentType(data);
        await fetchDoctypesDetailByProject();
        return newDoctype;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create document type";
        setError(errorMessage);
        console.error("Error creating document type:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchDoctypesDetailByProject],
  );

  const updateDoctype = useCallback(
    async (
      docTypeId: string,
      data: UpdateDocumentTypeRequest,
    ): Promise<DocumentTypeDetailViewModel> => {
      try {
        setIsLoading(true);
        setError(null);
        const updatedDoctype = await docTypeApi.updateDocumentType(
          docTypeId,
          data,
        );
        await fetchDoctypesDetailByProject();
        return updatedDoctype;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update document type";
        setError(errorMessage);
        console.error("Error updating document type:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchDoctypesDetailByProject],
  );

  const deleteDoctype = useCallback(
    async (docTypeId: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        await docTypeApi.deleteDocumentType(docTypeId);
        await fetchDoctypesDetailByProject();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete document type";
        setError(errorMessage);
        console.error("Error deleting document type:", err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchDoctypesDetailByProject],
  );

  return {
    doctypesDetail,
    isLoading,
    error,
    fetchDoctypesDetailByProject,
    createDoctype,
    updateDoctype,
    deleteDoctype,
  };
};
